
// Table_2.jsx : 주문품목(목록)

import { useState, useRef, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth } from "../../context/AuthContext";
import "../../styles/Table.css";

const Table_2 = ({
  userType,
  userId,
  orderBookEditMode,
  orderItemList,
  setProductQuery,
  clientItemList,
  selectedProductCode,
  setSelectedProductCode,
  setSelectedOrderItemRowOIserial,
  onModelNameSelect,
  setShowTable_3,
  setShowDeleteModal,
  setTargetTableToDelete,
  onClickItemDeleteButton,
  setSelectedQty,
  columnWidths = [
    "90px",
    "200px",
    "120px",
    "80px",
    "90px",
    "90px",
    "120px",
    "80px",
  ],
  columnAligns = [
    "center",
    "left",
    "left",
    "right",
    "right",
    "right",
    "center",
    "center",
  ],

}) => {
  const { Server_IP, token } = useAuth();
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const pagesPerGroup = 5;
  const tableContainerRef = useRef(null);
  const selectedRowRef = useRef(null);
  const [quantities, setQuantities] = useState({});

  // 선택 행이 바뀔 때 스크롤로 화면에 보이도록 수동 계산
  useEffect(() => {
    const row = selectedRowRef.current;
    const container = tableContainerRef.current;
    if (!row || !container) return;

    // sticky 헤더 높이 측정
    const thead = container.querySelector("thead");
    const theadHeight = thead ? thead.offsetHeight : 0;

    const rowTop = row.offsetTop;
    const rowBottom = rowTop + row.offsetHeight;
    const visibleTop = container.scrollTop + theadHeight;
    const visibleBottom = container.scrollTop + container.clientHeight;

    if (rowTop < visibleTop) {
      container.scrollTop = rowTop - theadHeight;
    } else if (rowBottom > visibleBottom) {
      container.scrollTop = rowBottom - container.clientHeight;
    }
  }, [selectedProductCode]);

  const totalPages = Math.ceil(
    (orderItemList?.length || 0) / itemsPerPage
  );

  const totalGroups = Math.ceil(totalPages / pagesPerGroup);
  const pageGroup = Math.floor((currentPage - 1) / pagesPerGroup);

  const startPage = pageGroup * pagesPerGroup + 1;
  const endPage = Math.min(startPage + pagesPerGroup - 1, totalPages);

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = Array.isArray(orderItemList)
    ? orderItemList.slice(indexOfFirstItem, indexOfLastItem)
    : [];

  const goToPage = (number) => {
    setSelectedProductCode(null); // 🚫 자동 페이지 이동 차단
    setProductQuery("");
    setCurrentPage(number);
  };

  const handlePrevGroup = () => {
    if (pageGroup > 0) {
      setSelectedProductCode(null); // 🚫 자동 페이지 이동 차단
      setProductQuery("");
      setCurrentPage((pageGroup - 1) * pagesPerGroup + 1);
    }
  };

  const handleNextGroup = () => {
    if (pageGroup < totalGroups - 1) {
      setSelectedProductCode(null); // 🚫 자동 페이지 이동 차단
      setProductQuery("");
      setCurrentPage((pageGroup + 1) * pagesPerGroup + 1);
    }
  };

  const handleOrderItemRowClick = useCallback((data) => {
    setSelectedProductCode(data.ProductCode);
    setSelectedOrderItemRowOIserial(data.OIserial);

    if (typeof onModelNameSelect === "function") {
      onModelNameSelect(data.ModelName || "");
    }
    setProductQuery(data.ModelName || "");
  }, [setSelectedProductCode, setSelectedOrderItemRowOIserial, onModelNameSelect, setProductQuery]);

  // Up/Down 키 핸들러
  const handleKeyDown = useCallback(
    (e) => {
      // input 요소에 포커스가 있으면 행 이동 무시
      if (document.activeElement && document.activeElement.tagName === "INPUT") return;

      if (e.key !== "ArrowUp" && e.key !== "ArrowDown") return;
      e.preventDefault();

      const list = Array.isArray(orderItemList) ? orderItemList : [];
      const currentIndex = list.findIndex(
        (item) => item.ProductCode === selectedProductCode
      );

      let nextIndex;
      if (currentIndex === -1) {
        nextIndex = 0;
      } else if (e.key === "ArrowUp") {
        nextIndex = Math.max(0, currentIndex - 1);
      } else {
        nextIndex = Math.min(list.length - 1, currentIndex + 1);
      }

      if (nextIndex === currentIndex && currentIndex !== -1) return;

      const nextItem = list[nextIndex];
      if (!nextItem) return;

      // 페이지 전환 처리
      const nextPage = Math.floor(nextIndex / itemsPerPage) + 1;
      if (nextPage !== currentPage) {
        setCurrentPage(nextPage);
      }

      handleOrderItemRowClick(nextItem);
    },
    [orderItemList, selectedProductCode, currentPage, itemsPerPage, handleOrderItemRowClick]
  );

  const handleRowDoubleClick = (data) => {
    setSelectedOrderItemRowOIserial(data.OIserial);
    setSelectedQty(data.Qty);
    setShowTable_3(true);
  };

  // 컨텍스트 메뉴 상태
  const [contextMenu, setContextMenu] = useState({ visible: false, x: 0, y: 0, row: null });

  // 치료재료 모달 상태
  const [treatmentModalOpen, setTreatmentModalOpen] = useState(false);
  const [treatmentModalQueryLabel, setTreatmentModalQueryLabel] = useState("");
  const [treatmentItems, setTreatmentItems] = useState([]);
  const [treatmentRaw, setTreatmentRaw] = useState(null);
  const [isTreatmentLoading, setIsTreatmentLoading] = useState(false);

  const hideContextMenu = useCallback(() => setContextMenu({ visible: false, x: 0, y: 0, row: null }), []);

  useEffect(() => {
    const onGlobalClick = (e) => {
      if (contextMenu.visible) hideContextMenu();
    };
    window.addEventListener("click", onGlobalClick);
    return () => window.removeEventListener("click", onGlobalClick);
  }, [contextMenu.visible, hideContextMenu]);

  const closeTreatmentModal = useCallback(() => {
    setTreatmentModalOpen(false);
    setTreatmentModalQueryLabel("");
    setTreatmentItems([]);
    setTreatmentRaw(null);
  }, []);
  const fetchTreatmentItems = useCallback(async ({ itmNm, mcatCd } = {}) => {
    setIsTreatmentLoading(true);
    try {
      const requestUrl = `${Server_IP}/api/public/therapeutic-materials`;

      const candidateList = [];
      if (mcatCd) {
        candidateList.push({ numOfRows: 500, mcatCd });
      } else if (itmNm) {
        candidateList.push({ numOfRows: 500, itmNm });
        candidateList.push({ numOfRows: 500 });
      } else {
        candidateList.push({ numOfRows: 500 });
      }

      const buildDisplayRows = (respData) => {
        const item = respData?.items && Array.isArray(respData.items) && respData.items.length > 0
          ? (respData.items[0] || {})
          : {};

        return [
          { key: "impEntpNm", label: "업체명", value: item.impEntpNm },
          { key: "itmNm", label: "품목명", value: item.itmNm },
          { key: "ldgrpCd", label: "대분류군코드", value: item.ldgrpCd },
          { key: "ldgrpCdNm", label: "대분류군코드명", value: item.ldgrpCdNm },
          { key: "ldivCd", label: "대분류코드", value: item.ldivCd },
          { key: "ldivCdNm", label: "대분류코드명", value: item.ldivCdNm },
          { key: "mcatCd", label: "재료대코드", value: item.mcatCd },
          { key: "mdivCd", label: "중분류코드", value: item.mdivCd },
          { key: "mdivCdNm", label: "중분류코드명", value: item.mdivCdNm },
          { key: "mnfEntpNm", label: "제조업체명", value: item.mnfEntpNm },
          { key: "mxUnprc", label: "상한단가", value: item.mxUnprc },
          { key: "nomNm", label: "규격명", value: item.nomNm },
          { key: "payTpNm", label: "급여구분명", value: item.payTpNm },
        ];
      };

      let loadedRows = [];
      for (const params of candidateList) {
        const cleanParams = Object.fromEntries(
          Object.entries(params).filter(([, value]) => String(value || "").trim()),
        );

        const resp = await axios.get(requestUrl, {
          params: cleanParams,
          headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        });

        loadedRows = buildDisplayRows(resp?.data);
        setTreatmentRaw(resp?.data || null);
        if (loadedRows.length > 0) {
          break;
        }
      }

      setTreatmentItems(loadedRows);
    } catch (err) {
      console.error("치료재료 조회 실패:", err);
      setTreatmentItems([]);
      setTreatmentRaw(null);
    } finally {
      setIsTreatmentLoading(false);
    }
  }, [Server_IP, token]);

  const openTreatmentModalForRow = useCallback((row) => {
    const label = [row?.Manufacture, row?.ItemName, row?.ModelName]
      .map((v) => String(v || "").trim())
      .filter(Boolean)
      .join(" / ") || row?.ItemName || row?.ModelName || "조회";
    const edi = row?.EDICode || row?.ediCode || row?.edi || undefined;

    setTreatmentModalQueryLabel(label);
    setTreatmentModalOpen(true);
    void fetchTreatmentItems({
      mcatCd: edi,
      itmNm: row?.ItemName || row?.ModelName || undefined,
    });
  }, [fetchTreatmentItems]);

  const handleRowContextMenu = (e, row) => {
    e.preventDefault();
    e.stopPropagation();
    setSelectedProductCode(row.ProductCode);
    setSelectedOrderItemRowOIserial(row.OIserial);
    setProductQuery(row.ModelName || "");
    setContextMenu({ visible: true, x: e.clientX, y: e.clientY, row });
  };

  const formatNumber = (value) => {
    if (!value) return "";
    const number = parseInt(value.toString().replace(/,/g, ""), 10);
    return isNaN(number) ? "" : number.toLocaleString();
  };

  const handleFormattedInputChange = (id, inputValue) => {
    const numericValue = inputValue.replace(/,/g, "");
    if (/^\d*$/.test(numericValue) && numericValue.length <= 6) {
      setQuantities((prev) => ({
        ...prev,
        [id]: numericValue,
      }));
    }
  };
  // clientItemList에서 없는 품목 코드 감지
  const isProductCodeInClientList = (code) =>
    Array.isArray(clientItemList) &&
    clientItemList.some((item) => item.ProductCode === code);

  useEffect(() => {
    if (selectedProductCode) {
      const selectedItemIndex = orderItemList.findIndex(
        (item) => item.ProductCode === selectedProductCode
      );

      if (selectedItemIndex !== -1) {
        const targetPage = Math.floor(selectedItemIndex / itemsPerPage) + 1;
        if (targetPage !== currentPage) {
          setCurrentPage(targetPage);
        }
      }
    }
  }, [selectedProductCode, orderItemList]);

  const headers = [
    "품목코드",
    "품목명",
    "모델명",
    "수량",
    "단가",
    "금액",
    "공급구분",
    "작업",
  ];

  

  const totalAmount = Array.isArray(orderItemList)

    ? orderItemList.reduce((sum, item) => {
      const amount = parseInt(
        item.Amount?.toString().replace(/,/g, "") || "0",
        10
      );
      return sum + (isNaN(amount) ? 0 : amount);
    }, 0)
    : 0;

  return (
    <>
      {/* 외부 wrapper: flex-column으로 테이블+페이지네이션 분리 */}
      <div
        style={{ display: "flex", flexDirection: "column", height: "750px" }}
        tabIndex={0}
        onKeyDown={handleKeyDown}
        onFocus={(e) => (e.currentTarget.style.outline = "none")}
      >
        <div className="table-container" ref={tableContainerRef} style={{ flex: 1, height: "auto", outline: "none", overflow: "auto" }}>
          <table className="table table-hover">
            <thead>
              <tr>
                {headers.map((header, idx) => {
                  if (idx === 6 && userType !== "manager") return null;
                  return (
                    <th
                      key={header}
                      style={{ width: columnWidths[idx] || "auto" }}
                    >
                      {header}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {Array.isArray(currentItems) &&
                currentItems
                  .filter((d) => d && d.ProductCode) // undefined나 잘못된 객체 제거
                  .map((d) => {
                    const isSelected = selectedProductCode === d.ProductCode;
                    const ediCode = d.EDICode || d.ediCode;
                      return (
                      <tr
                        key={d.OIserial}
                        ref={isSelected ? selectedRowRef : null}
                        onClick={() => handleOrderItemRowClick(d)}
                        onDoubleClick={() => handleRowDoubleClick(d)}
                        onContextMenu={(e) => handleRowContextMenu(e, d)}
                        className={isSelected ? "selected-row" : ""}
                      >
                        <td className={`align-${columnAligns[0]}`}>
                          <div>{d.ProductCode}</div>
                          {ediCode && String(ediCode).length <= 8 ? (
                            <div style={{ color: '#c62828', fontSize: 12, marginTop: 4 }}>EDI : {ediCode}</div>
                          ) : null}
                        </td>
                        <td className={`align-${columnAligns[1]}`}>{d.ItemName}</td>
                        <td className={`align-${columnAligns[2]}`}>
                          {d.ModelName}
                        </td>

                        <td className={`align-${columnAligns[3]}`}>
                          {Number(d.RemainQty) !== 0 && (
                            <div className="remain-qty-highlight">
                              잔량: {formatNumber(d.RemainQty)}
                            </div>
                          )}
                          <input
                            className="form-control"
                            type="text"
                            disabled
                            value={formatNumber(quantities[d.ProductCode] || d.Qty)}
                            onChange={(e) =>
                              handleFormattedInputChange(
                                d.ProductCode,
                                e.target.value
                              )
                            }
                            style={{
                              width: columnWidths[3],
                              textAlign: "right",
                            }}
                          />
                        </td>
                        <td className={`align-${columnAligns[4]}`}>
                          {formatNumber(d.DiscountPrice)}
                        </td>
                        <td className={`align-${columnAligns[5]}`}>
                          {formatNumber(d.Amount)}
                        </td>
                        {userType === "manager" && (
                          <td className={`align-${columnAligns[6]}`}>
                            {{
                              1: "출고",
                              2: "반품",
                              3: "폐기",
                              4: "일반반품",
                              5: "회수반품",
                              6: "임대반품",
                            }[d.SuplyFlag?.[0]] || d.SuplyFlag}
                          </td>
                        )}
                        <td className={`align-${columnAligns[7]}`}>
                          <div
                            style={{
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "flex-end", // 오른쪽 정렬
                            }}
                          >
                            {userId &&
                              !isProductCodeInClientList(d.ProductCode) && (
                                <span className="missing-label">목록에 없음</span>
                              )}

                            {orderBookEditMode && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  e.preventDefault();
                                  setTargetTableToDelete("ITEM");
                                  onClickItemDeleteButton(d.OIserial);
                                  setShowDeleteModal(true);
                                }}
                                className="btn-blue-outline"
                              >
                                삭제
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
            </tbody>
          </table>
        </div>

        {/* Context menu */}
        {contextMenu.visible && (
          <div
            style={{
              position: "fixed",
              left: contextMenu.x,
              top: contextMenu.y,
              background: "#fff",
              border: "1px solid #ddd",
              boxShadow: "0 2px 8px rgba(0,0,0,0.12)",
              zIndex: 2000,
              minWidth: 160,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              style={{ display: "block", width: "100%", padding: "8px 12px", border: "none", background: "transparent", textAlign: "left" }}
              onClick={() => {
                  hideContextMenu();
                    hideContextMenu();
                    openTreatmentModalForRow(contextMenu.row);
              }}
            >
              치료재료정보
            </button>
          </div>
        )}

        {/* Treatment modal */}
        {treatmentModalOpen && (
          <div
            style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.45)", zIndex: 2100 }}
            onClick={closeTreatmentModal}
          >
            <div
              role="dialog"
              aria-modal="true"
              aria-label="치료재료정보 모달"
              onClick={(e) => e.stopPropagation()}
              style={{
                width: 820,
                maxWidth: "calc(100% - 40px)",
                maxHeight: "80%",
                overflow: "auto",
                margin: "5% auto",
                background: "#fff",
                borderRadius: 12,
                boxShadow: "0 24px 60px rgba(15,23,42,0.22)",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16, padding: "16px 18px 12px", borderBottom: "1px solid #e2e8f0", background: "linear-gradient(180deg, #f8fafc 0%, #ffffff 100%)", borderTopLeftRadius: 12, borderTopRightRadius: 12 }}>
                <div style={{ flex: "1 1 auto" }}>
                  <div style={{ fontSize: 18, fontWeight: 700, color: "#0f172a", lineHeight: 1.2 }}>
                    치료재료정보 — {treatmentModalQueryLabel || "전체 조회"}
                  </div>
                  <div style={{ marginTop: 4, fontSize: 12, color: "#64748b" }}>
                    {isTreatmentLoading
                      ? "조회 중..."
                      : `신고제품명: ${(
                          treatmentRaw && Array.isArray(treatmentRaw.items) && treatmentRaw.items.length > 0
                            ? String(treatmentRaw.items[0].itmNm || "").trim() || treatmentModalQueryLabel || ""
                            : treatmentModalQueryLabel || ""
                        )}`}
                  </div>
                </div>
                <div style={{ flex: "0 0 auto", marginLeft: 12 }}>
                  <img src="/images/publicLicenceNotification.png" alt="라이선스 고지" style={{ height: 40, display: "block" }} />
                  <div style={{ textAlign: "center", fontSize: "6pt", color: "#64748b", marginTop: 4 }}>
                    심평원-치료재료정보 제공자료 기준입니다.
                  </div>
                </div>
              </div>

              <div style={{ padding: 18 }}>
                {isTreatmentLoading ? (
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: 220, flexDirection: "column" }}>
                    <div style={{ width: 40, height: 40, borderRadius: "50%", border: "4px solid #cbd5e1", borderTopColor: "#1e40af", animation: "ps-spin 1s linear infinite", marginBottom: 8 }} />
                    <div style={{ color: "#334155" }}>데이터를 불러오는 중입니다...</div>
                  </div>
                ) : (
                  <div style={{ overflow: "auto", maxHeight: 420 }}>
                    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "12px" }}>
                    <thead>
                      <tr>
                        <th style={{ textAlign: "center", padding: 8, borderBottom: "1px solid #e6edf3" }}>NO.</th>
                        <th style={{ textAlign: "center", padding: 8, borderBottom: "1px solid #e6edf3" }}>항목</th>
                        <th style={{ textAlign: "center", padding: 8, borderBottom: "1px solid #e6edf3" }}>내용</th>
                      </tr>
                    </thead>
                    <tbody>
                      {treatmentRaw && Array.isArray(treatmentRaw.items) && treatmentRaw.items.length > 0 ? (
                        treatmentItems.map((row, idx) => {
                          const displayValue = row.key === "mxUnprc"
                            ? (Number.isFinite(Number(row.value)) ? Number(row.value).toLocaleString("ko-KR") : "")
                            : String(row.value ?? "");

                          return (
                            <tr key={row.key}>
                              <td style={{ padding: 8, borderBottom: "1px solid #f1f5f9", verticalAlign: "top", textAlign: "center" }}>{idx + 1}</td>
                              <td style={{ padding: 8, borderBottom: "1px solid #f1f5f9", verticalAlign: "top", textAlign: "center" }}>{row.label}</td>
                              <td style={{ padding: 8, borderBottom: "1px solid #f1f5f9", verticalAlign: "top", whiteSpace: "pre-wrap", wordBreak: "break-word", textAlign: "center" }}>{displayValue}</td>
                            </tr>
                          );
                        })
                      ) : (
                        <tr>
                          <td style={{ padding: 12 }} colSpan={3}>
                            항목이 없습니다.
                          </td>
                        </tr>
                      )}
                    </tbody>
                    </table>
                  </div>
                )}
              </div>
              <div style={{ padding: "0 18px 18px", display: "flex", justifyContent: "flex-end" }}>
                <button
                  type="button"
                  onClick={closeTreatmentModal}
                  style={{
                    minWidth: 90,
                    height: 38,
                    borderRadius: 8,
                    border: "1px solid #cbd5e1",
                    background: "#ffffff",
                    color: "#0f172a",
                    fontWeight: 700,
                    cursor: "pointer",
                  }}
                >
                  닫기
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="pagination-and-container" style={{ flexShrink: 0 }}>
          <div className="summary-box">
            <div>주문 품목수: {orderItemList?.length || 0}개</div>
            <div>합 계 : {formatNumber(totalAmount)}원</div>
          </div>

          <div className="pagination">
            <button onClick={handlePrevGroup} disabled={pageGroup <= 0}>
              &laquo; 이전
            </button>

            {Array.from({ length: Math.max(0, endPage - startPage + 1) }, (_, i) => {
              const pageNum = startPage + i;
              return (
                <button
                  key={pageNum}
                  onClick={() => goToPage(pageNum)}
                  className={currentPage === pageNum ? "active" : ""}
                >
                  {pageNum}
                </button>
              );
            })}

            <button
              onClick={handleNextGroup}
              disabled={pageGroup >= totalGroups - 1 || totalGroups === 0}
            >
              다음 &raquo;
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default Table_2;
